import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import logoImage from '../assets/logo.png';

// mode: 'login' | 'register' | 'forgot' | 'reset'
export default function AuthPage() {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ username: '', email: '', password: '', otp: '', newPassword: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const switchMode = (m) => {
    setMode(m);
    setError('');
    setSuccess('');
  };

  // ── Login ──────────────────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await login(form.email, form.password);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // ── Register ───────────────────────────────────────────────────────────────
  const handleRegister = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    if (!form.username.trim()) return (setError('Username required'), setLoading(false));
    if (form.password.length < 6) return (setError('Password must be at least 6 characters'), setLoading(false));
    try {
      await register(form.username, form.email, form.password);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // ── Forgot — send OTP ──────────────────────────────────────────────────────
  const handleForgot = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await authAPI.forgotPassword(form.email);
      setSuccess('OTP sent! Check your email (or server console in dev mode). Enter it below.');
      switchMode('reset');
      // keep email in form for next step
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  // ── Reset — verify OTP + set new password ─────────────────────────────────
  const handleReset = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    if (form.newPassword !== form.confirmPassword)
      return (setError('Passwords do not match'), setLoading(false));
    if (form.newPassword.length < 6)
      return (setError('Password must be at least 6 characters'), setLoading(false));
    try {
      await authAPI.resetPassword(form.email, form.otp, form.newPassword);
      setSuccess('Password reset! You can now sign in with your new password.');
      switchMode('login');
    } catch (err) {
      setError(err.response?.data?.error || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  const titles = {
    login:    { title: 'Welcome back', sub: 'Sign in to your trading account' },
    register: { title: 'Create account', sub: 'Start with 1,000 points to trade' },
    forgot:   { title: 'Forgot password', sub: 'Enter your email to receive a 6-digit OTP' },
    reset:    { title: 'Reset password', sub: 'Enter the OTP from your email and your new password' },
  };

  const { title, sub } = titles[mode];

  return (
    <div className="auth-page">
      <div className="auth-card">
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <img src={logoImage} alt="IPLTrade" style={{ width: 160, maxWidth: '72%', height: 'auto', objectFit: 'contain', marginBottom: 10 }} />
          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: 4 }}>
            Trade IPL Player Stocks in Real Time
          </div>
        </div>

        <div className="auth-title">{title}</div>
        <div className="auth-subtitle">{sub}</div>

        {error   && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {/* ── LOGIN ── */}
        {mode === 'login' && (
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={form.email} onChange={set('email')} placeholder="you@example.com" required />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input className="form-input" type="password" value={form.password} onChange={set('password')} placeholder="••••••••" required />
            </div>
            <div style={{ textAlign: 'right', marginBottom: 12, marginTop: -6 }}>
              <button type="button" onClick={() => switchMode('forgot')} style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: '0.8rem' }}>
                Forgot password?
              </button>
            </div>
            <button className="btn-primary" type="submit" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        )}

        {/* ── REGISTER ── */}
        {mode === 'register' && (
          <form onSubmit={handleRegister}>
            <div className="form-group">
              <label className="form-label">Username</label>
              <input className="form-input" value={form.username} onChange={set('username')} placeholder="tradingpro" required />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={form.email} onChange={set('email')} placeholder="you@example.com" required />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input className="form-input" type="password" value={form.password} onChange={set('password')} placeholder="Min. 6 characters" required />
            </div>
            <button className="btn-primary" type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Account'}
            </button>
          </form>
        )}

        {/* ── FORGOT — send OTP ── */}
        {mode === 'forgot' && (
          <form onSubmit={handleForgot}>
            <div className="form-group">
              <label className="form-label">Your email address</label>
              <input className="form-input" type="email" value={form.email} onChange={set('email')} placeholder="you@example.com" required />
            </div>
            <button className="btn-primary" type="submit" disabled={loading}>
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </button>
            <div style={{ textAlign: 'center', marginTop: 12, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              Already have an OTP?{' '}
              <button type="button" onClick={() => switchMode('reset')} style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: '0.8rem' }}>
                Enter it here
              </button>
            </div>
          </form>
        )}

        {/* ── RESET — verify OTP + new password ── */}
        {mode === 'reset' && (
          <form onSubmit={handleReset}>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={form.email} onChange={set('email')} placeholder="you@example.com" required />
            </div>
            <div className="form-group">
              <label className="form-label">OTP (6 digits from email)</label>
              <input
                className="form-input"
                value={form.otp}
                onChange={set('otp')}
                placeholder="123456"
                maxLength={6}
                pattern="[0-9]{6}"
                inputMode="numeric"
                required
                style={{ letterSpacing: 8, fontSize: '1.2rem', textAlign: 'center' }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input className="form-input" type="password" value={form.newPassword} onChange={set('newPassword')} placeholder="Min. 6 characters" required />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <input className="form-input" type="password" value={form.confirmPassword} onChange={set('confirmPassword')} placeholder="Repeat password" required />
            </div>
            <button className="btn-primary" type="submit" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
            <div style={{ textAlign: 'center', marginTop: 10, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              Didn't get an OTP?{' '}
              <button type="button" onClick={() => switchMode('forgot')} style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: '0.8rem' }}>
                Resend
              </button>
            </div>
          </form>
        )}

        {/* ── Toggle login/register ── */}
        <div className="auth-toggle" style={{ marginTop: '1.25rem' }}>
          {mode === 'login' || mode === 'forgot' || mode === 'reset' ? (
            <>No account?{' '}
              <button onClick={() => switchMode('register')}>Register free</button>
            </>
          ) : (
            <>Already have one?{' '}
              <button onClick={() => switchMode('login')}>Sign in</button>
            </>
          )}
          {(mode === 'forgot' || mode === 'reset') && (
            <span style={{ marginLeft: 10 }}>
              ·{' '}
              <button onClick={() => switchMode('login')} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.82rem' }}>
                Back to login
              </button>
            </span>
          )}
        </div>

        {/* Info box */}
        <div style={{ marginTop: '1.25rem', padding: '12px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
          <strong style={{ color: 'var(--text-secondary)' }}>Trading rules:</strong>{' '}
          2,000 starting points · Max 200 trades per season · 1 unit per player ·
          Trading open 12 AM–3 PM IST · Closed 3 PM–midnight (match hours) ·
          Prices update at 7:30 PM &amp; 11:59 PM IST
        </div>
      </div>
    </div>
  );
}
